import React from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import Logo from '../assets/logo.svg?react';
import { AuthContext } from '../auth';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NavLink, useNavigate } from 'react-router-dom';
import { client } from '../api';
import { resetAnalyticsIdentity } from 'shared-utils/analytics';
import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuSection,
  PopoverMenuHeading,
  PopoverMenuItems,
  PopoverMenuItem,
  PopoverMenuItemButton,
  PopoverMenuHairline,
} from 'design-system/src/components/PopoverMenu/PopoverMenu';
import SettingsOutline from 'design-system/src/assets/icons/settingsOutline.svg?react';
import PieChart from '../assets/pieChart.svg?react';
import PieChartInverted from '../assets/pieChartInverted.svg?react';
import Tasks from '../assets/tasks.svg?react';
import TasksInverted from '../assets/tasksInverted.svg?react';
import CheckingBox from 'design-system/src/assets/icons/checkingBox.svg?react';
import CheckingBoxInverted from 'design-system/src/assets/icons/checkingBoxInverted.svg?react';
import Drug from 'design-system/src/assets/icons/drug.svg?react';
import DrugInverted from 'design-system/src/assets/icons/drugInverted.svg?react';
import Alarm from 'design-system/src/assets/icons/alarm.svg?react';
import AlarmInverted from 'design-system/src/assets/icons/alarmInverted.svg?react';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { focusVisibleStyles, standardTransitionStyles } from 'design-system/src/sharedStyles';
import NotificationDot from 'design-system/src/components/NotificationDot/NotificationDot';
import Notification from 'design-system/src/assets/icons/notification.svg?react';
import NotificationInverted from 'design-system/src/assets/icons/notificationInverted.svg?react';
import { createTrackingProps } from 'shared-utils/analytics';
import LightBulb from 'design-system/src/assets/icons/lightBulb.svg?react';
import LightBulbFilled from 'design-system/src/assets/icons/lightBulbFilled.svg?react';
import SkipEhrConnectionModal from './SkipEhrConnectionModal';
import { EhrUpsellCard } from './EhrUpsellCard';
import { GetStartedPanel } from './onboarding';
import { isValidEhr } from '../utils';

const buttonStyles = `
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--grayscale-900);
  background-color: transparent;
  border-radius: var(--border-radius-medium);
  border: none;

  transition: background 0.2s ease-out, box-shadow 0.2s ease-out;

  &:focus-visible {
    box-shadow: 0px 0px 0px 1px var(--white), 0px 0px 0px 3px var(--purple-700);
    // for Windows High Contrast Mode, which removes box shadows but makes all borders
    // 100% opacity black
    outline: 1px solid rgba(0,0,0,0);
  }
`;

const navButtonStyles = `
  ${buttonStyles}

  // TODO(design-system): use body medium typography styles
  font-size: 0.9375rem;
  font-style: normal;
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: 0.009375rem;

  &:hover {
    // TODO(tokens): Replace legacy color token --cream2 with numbered equivalent once available.
    background: var(--cream2);
  }
  &:active, &:focus-visible {
    // TODO(tokens): Replace legacy color token --cream3 with numbered equivalent once available.
    background: var(--cream3);
  }
`;

const StyledNavLink = styled(NavLink)`
  ${navButtonStyles}

  text-decoration: none;
  display: block;
`;

const SettingsButton = styled(PopoverMenuButton)`
  ${navButtonStyles}

  font-family: 'Space Grotesk', sans-serif;
  padding: 0.75rem;
`;

const NavButton = styled.button`
  ${navButtonStyles}

  text-decoration: none;
  padding: 0;
`;

const NavSidebarContainer = styled.div<{ $bannersHeight: number }>`
  // TODO: move to core css file, apply to everything
  box-sizing: border-box;

  width: var(--nav-sidebar-width);
  position: fixed;
  height: calc(100vh - ${({ $bannersHeight }) => $bannersHeight}px);
  height: calc(100dvh - ${({ $bannersHeight }) => $bannersHeight}px); // iOS safari
  top: ${({ $bannersHeight }) => $bannersHeight}px;
  // z-index lifts the sidebar above the main page content
  z-index: 2;

  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  background: var(--surface-100);
  border-right: 1px solid var(--grayscale-100);

  overflow-y: auto;
  // don't scroll main content when scrolling the sidebar
  overscroll-behavior: none;
`;

const LogoLink = styled.a`
  // TODO: move to core css file, apply to everything
  box-sizing: border-box;
  height: 3rem;
  padding: 0.875rem 1rem;
  // for the focus outline
  border-radius: var(--border-radius-medium);
  ${standardTransitionStyles}

  ${focusVisibleStyles}
`;

const NavItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  justify-content: space-between;
  width: 100%;
  height: 100%;
`;

const Tabs = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const NavItemContainer = styled.div<{ $isActive: boolean }>`
  color: var(--purple-900);

  padding: 0.75rem;
  display: flex;
  gap: 0.75rem;
  border-radius: var(--border-radius-medium);
  // TODO(tokens): Replace legacy color token --cream3 with numbered equivalent once available.
  ${({ $isActive }) => ($isActive ? 'background-color: var(--cream3)' : '')}
`;

const NavItemIcon = styled.div`
  display: flex;
  align-items: center;
  // Most of the icons are 16px wide, but one of them is 18px, so we'll use that
  // for consistency
  width: 18px;
`;

const NavItemText = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
`;

const StyledHairline = styled(Hairline)`
  margin: 16px 0;
`;

const InnerContainer = styled.div`
  position: relative;
`;

type NavItemProps = {
  icon: React.ReactNode | null;
  // The version of the icon that appears when this item is active
  // If not provided, the regular icon will be used
  activeIcon?: React.ReactNode;
  iconLocation?: 'left' | 'right';
  text: string | React.ReactNode;
  isActive?: boolean;
};

const NavItem = ({ icon, activeIcon, text, isActive = false, iconLocation = 'left', ...rest }: NavItemProps) => {
  const iconElement = icon ? <NavItemIcon>{isActive && activeIcon ? activeIcon : icon}</NavItemIcon> : null;

  return (
    <NavItemContainer $isActive={isActive} {...rest}>
      {iconLocation === 'left' && iconElement}
      <NavItemText>{text}</NavItemText>
      {iconLocation === 'right' && iconElement}
    </NavItemContainer>
  );
};

const SettingsMenu = ({
  email,
  hasSettingsPage,
  practiceName,
  hasPadPreferences,
  showTos,
}: {
  email?: string;
  hasSettingsPage?: boolean;
  practiceName?: string | null;
  hasPadPreferences?: boolean;
  showTos?: boolean;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isDownloadingTos, setIsDownloadingTos] = React.useState(false);

  // Holds the popover menu's close function so we can dismiss it after the TOS download completes
  const closeRef = React.useRef<(() => void) | null>(null);

  const handleTosDownload = async () => {
    setIsDownloadingTos(true);
    let url: string | null = null;
    let a: HTMLAnchorElement | null = null;
    try {
      const response = await client.get('/signup/tos-download', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      url = window.URL.createObjectURL(blob);
      a = document.createElement('a');
      a.href = url;
      // Extract the filename from the Content-Disposition header, falling back to a default
      const contentDisposition = response.headers['content-disposition'] ?? '';
      const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
      a.download = filenameMatch?.[1] ?? 'terms_of_service.pdf';
      document.body.appendChild(a);
      a.click();
      closeRef.current?.();
    } catch {
      toast.custom(() => <StandardErrorToast />);
    } finally {
      if (url) {
        window.URL.revokeObjectURL(url);
      }
      if (a && document.body.contains(a)) {
        document.body.removeChild(a);
      }
      setIsDownloadingTos(false);
    }
  };

  const logout = useMutation({
    mutationFn: () => {
      return client.post('logout');
    },
    onSuccess: async () => {
      stytch.session.revoke();
      resetAnalyticsIdentity();

      await queryClient.invalidateQueries({ queryKey: ['checkLogin'] });
      navigate('/login');
    },
  });

  const stytch = useStytchB2BClient();

  return (
    <PopoverMenu>
      <SettingsButton>
        <SettingsOutline width={20} height={20} aria-hidden />
        Settings
      </SettingsButton>

      <PopoverMenuItems width="16rem" {...createTrackingProps({ unmask: true })}>
        <PopoverMenuSection>
          {hasSettingsPage && (
            <>
              <PopoverMenuItem>
                {(itemProps) => (
                  <PopoverMenuItemButton
                    {...itemProps}
                    onClick={() => {
                      itemProps.close?.();
                      navigate('/settings?tab=notifications');
                    }}
                    {...createTrackingProps({ elementName: 'Settings Menu: Notifications & Filters' })}
                  >
                    Notifications & Filters
                  </PopoverMenuItemButton>
                )}
              </PopoverMenuItem>

              <PopoverMenuItem>
                {(itemProps) => (
                  <PopoverMenuItemButton
                    {...itemProps}
                    onClick={() => {
                      itemProps.close?.();
                      navigate('/settings?tab=users');
                    }}
                    {...createTrackingProps({ elementName: 'Settings Menu: Users' })}
                  >
                    Users
                  </PopoverMenuItemButton>
                )}
              </PopoverMenuItem>

              <PopoverMenuItem>
                {(itemProps) => (
                  <PopoverMenuItemButton
                    {...itemProps}
                    onClick={() => {
                      itemProps.close?.();
                      navigate('/settings?tab=offices');
                    }}
                    {...createTrackingProps({ elementName: 'Settings Menu: Offices' })}
                  >
                    Offices
                  </PopoverMenuItemButton>
                )}
              </PopoverMenuItem>

              <PopoverMenuItem>
                {(itemProps) => (
                  <PopoverMenuItemButton
                    {...itemProps}
                    onClick={() => {
                      itemProps.close?.();
                      navigate('/settings?tab=profile');
                    }}
                    {...createTrackingProps({ elementName: 'Settings Menu: Profile' })}
                  >
                    Profile
                  </PopoverMenuItemButton>
                )}
              </PopoverMenuItem>

              {hasPadPreferences && (
                <PopoverMenuItem>
                  {(itemProps) => (
                    <PopoverMenuItemButton
                      {...itemProps}
                      onClick={() => {
                        itemProps.close?.();
                        navigate('/settings?tab=benefitPreferences');
                      }}
                      {...createTrackingProps({ elementName: 'Settings Menu: Provider-Administered Drugs' })}
                    >
                      Provider-administered drugs
                    </PopoverMenuItemButton>
                  )}
                </PopoverMenuItem>
              )}

              <PopoverMenuHairline />
            </>
          )}

          {practiceName && <PopoverMenuHeading>{practiceName}</PopoverMenuHeading>}
          {email && <PopoverMenuHeading>{email}</PopoverMenuHeading>}

          {showTos && (
            <PopoverMenuItem>
              {(itemProps) => {
                closeRef.current = itemProps.close ?? null;
                return (
                  <PopoverMenuItemButton
                    {...itemProps}
                    disabled={isDownloadingTos}
                    onClick={(e) => {
                      // stop the menu from closing so the user can see the loading state
                      e.preventDefault();
                      handleTosDownload();
                    }}
                    {...createTrackingProps({ elementName: 'Settings Menu: Terms of Service' })}
                  >
                    {isDownloadingTos ? <LoadingSpinner height="17px" size="16px" /> : 'Download Terms of Service'}
                  </PopoverMenuItemButton>
                );
              }}
            </PopoverMenuItem>
          )}

          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={() => {
                  itemProps.close?.();
                  logout.mutate();
                }}
                {...createTrackingProps({ elementName: 'Settings Menu: Sign Out' })}
              >
                Sign out
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>
        </PopoverMenuSection>
      </PopoverMenuItems>
    </PopoverMenu>
  );
};

export const NavSidebar = ({ bannersHeight }: { bannersHeight: number }) => {
  const authContext = React.useContext(AuthContext);
  const isOnboarding = authContext.user?.tabs.includes('onboarding');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const shouldShowEhrUpsell = authContext.user?.feature_flags?.show_ehr_upsells && !isOnboarding;
  const ehrName = authContext.user?.ehr_name;
  const [isSkipEhrModalOpen, setIsSkipEhrModalOpen] = React.useState(false);
  const [isGetStartedPanelOpen, setIsGetStartedPanelOpen] = React.useState(false);
  // this is necessary for the detail pages to show the correct tab as active
  const isPrescriptionTab = window.location.pathname.includes('/rxs');
  const isTasksTab = window.location.pathname.includes('/tasks');
  const isPriorAuthReviewsTab = window.location.pathname.includes('/prior-auth-reviews');
  const isUsersTab = window.location.pathname.includes('/settings') && window.location.search.includes('tab=users');

  const hideEhrUpsellCard = async () => {
    await queryClient.invalidateQueries({ queryKey: ['checkLogin'] });
  };

  return (
    <NavSidebarContainer $bannersHeight={bannersHeight}>
      {/* Use anchor tag to force a full page reload in case the auth status has changed */}
      <LogoLink href="/">
        <Logo title="Home" />
      </LogoLink>
      {authContext.auth && (
        <NavItems>
          <Tabs {...createTrackingProps({ unmask: true })}>
            {isOnboarding && (
              <StyledNavLink to="/onboarding">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive}
                    icon={<LightBulb aria-hidden={true} />}
                    activeIcon={<LightBulbFilled aria-hidden={true} />}
                    text="Get started"
                  />
                )}
              </StyledNavLink>
            )}
            {authContext.user?.tabs.includes('prescriptions') && (
              <StyledNavLink to="/prescriptions">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive || isPrescriptionTab}
                    icon={<Drug aria-hidden={true} />}
                    activeIcon={<DrugInverted aria-hidden={true} />}
                    text="Prescriptions"
                    {...createTrackingProps({ elementName: 'Left Rail: Prescriptions' })}
                  />
                )}
              </StyledNavLink>
            )}
            {authContext.user?.tabs.includes('tasks') && (
              <StyledNavLink to="/tasks">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive || isTasksTab}
                    icon={<Tasks aria-hidden={true} />}
                    activeIcon={<TasksInverted aria-hidden={true} />}
                    text={
                      authContext.user?.notifications?.num_tasks_requiring_response ? (
                        <NotificationDot srOnlyText="there are tasks requiring response">Tasks</NotificationDot>
                      ) : (
                        'Tasks'
                      )
                    }
                    {...createTrackingProps({ elementName: 'Left Rail: Tasks' })}
                  />
                )}
              </StyledNavLink>
            )}
            {authContext.user?.tabs.includes('pa_reviews') && (
              <StyledNavLink to="/prior-auth-reviews">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive || isPriorAuthReviewsTab}
                    icon={<CheckingBox aria-hidden={true} />}
                    activeIcon={<CheckingBoxInverted aria-hidden={true} />}
                    text={
                      authContext.user?.notifications?.num_open_pa_reviews ? (
                        <NotificationDot srOnlyText="there are open PA reviews">PA Reviews</NotificationDot>
                      ) : (
                        'PA Reviews'
                      )
                    }
                    {...createTrackingProps({ elementName: 'Left Rail: PA Reviews' })}
                  />
                )}
              </StyledNavLink>
            )}
            {authContext.user?.tabs.includes('notifications') && (
              <StyledNavLink to="/notifications">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive}
                    icon={<Notification aria-hidden={true} />}
                    activeIcon={<NotificationInverted aria-hidden={true} />}
                    text={
                      authContext.user?.notifications?.num_portal_notifications ? (
                        <NotificationDot srOnlyText="there are open portal notifications">
                          Notifications
                        </NotificationDot>
                      ) : (
                        'Notifications'
                      )
                    }
                    {...createTrackingProps({ elementName: 'Left Rail: Notifications' })}
                  />
                )}
              </StyledNavLink>
            )}
            {authContext.user?.tabs.includes('prior_auths') && (
              <StyledNavLink to="/expiring-prior-auths">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive}
                    icon={<Alarm aria-hidden={true} />}
                    activeIcon={<AlarmInverted aria-hidden={true} />}
                    text="Expiring PAs"
                    {...createTrackingProps({ elementName: 'Left Rail: Expiring PAs' })}
                  />
                )}
              </StyledNavLink>
            )}
            {authContext.user?.tabs.includes('dashboard') && (
              <StyledNavLink to="/analytics">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive}
                    icon={<PieChart aria-hidden={true} />}
                    activeIcon={<PieChartInverted aria-hidden={true} />}
                    text="Analytics"
                    {...createTrackingProps({ elementName: 'Left Rail: Analytics' })}
                  />
                )}
              </StyledNavLink>
            )}

            {!isOnboarding && authContext.user?.feature_flags?.show_get_started_panel && (
              <>
                <StyledHairline />
                <NavButton onClick={() => setIsGetStartedPanelOpen(true)}>
                  <NavItem
                    isActive={isGetStartedPanelOpen}
                    icon={<LightBulb aria-hidden={true} />}
                    activeIcon={<LightBulbFilled aria-hidden={true} />}
                    text="Get started"
                  />
                </NavButton>
              </>
            )}

            <StyledHairline />

            <StyledNavLink to="/settings?tab=users">
              {/* we're not using isActive here because we only want to show the users tab as active when the user is on the users tab,
                  not just any settings page tab */}
              <NavItem
                isActive={isUsersTab}
                icon={null}
                text="Users"
                {...createTrackingProps({ elementName: 'Left Rail: Users' })}
              />
            </StyledNavLink>

            <StyledNavLink to="/referral">
              {({ isActive }) => (
                <NavItem
                  isActive={isActive}
                  icon={null}
                  text="Refer a practice"
                  {...createTrackingProps({ elementName: 'Left Rail: Referral' })}
                />
              )}
            </StyledNavLink>
            {authContext.user?.tabs.includes('fax_verification') && (
              <StyledNavLink to="/verify-fax">
                {({ isActive }) => (
                  <NavItem
                    isActive={isActive}
                    icon={null}
                    text="Fax verification"
                    {...createTrackingProps({ elementName: 'Left Rail: CMM Verification' })}
                  />
                )}
              </StyledNavLink>
            )}
            <StyledNavLink to="/help-center">
              {({ isActive }) => (
                <NavItem
                  isActive={isActive}
                  icon={null}
                  text="Help center"
                  {...createTrackingProps({ elementName: 'Left Rail: Help Center' })}
                />
              )}
            </StyledNavLink>
          </Tabs>

          <div className="flex w-full flex-col gap-6">
            {shouldShowEhrUpsell && isValidEhr(ehrName) && (
              <EhrUpsellCard
                ehr={ehrName}
                onConnect={() => navigate('/settings?tab=ehrIntegration')}
                onDismiss={() => setIsSkipEhrModalOpen(true)}
              />
            )}

            {authContext.auth && (
              <InnerContainer>
                <SettingsMenu
                  email={authContext.user?.email}
                  hasSettingsPage={authContext.user?.has_settings_page}
                  practiceName={authContext.user?.practice_name}
                  hasPadPreferences={authContext.user?.feature_flags?.has_pad_preferences}
                  showTos={authContext.user?.show_tos}
                />
              </InnerContainer>
            )}
          </div>
        </NavItems>
      )}
      {isSkipEhrModalOpen && (
        <SkipEhrConnectionModal onClose={() => setIsSkipEhrModalOpen(false)} onSuccess={hideEhrUpsellCard} />
      )}
      <GetStartedPanel isOpen={isGetStartedPanelOpen} onClose={() => setIsGetStartedPanelOpen(false)} />
    </NavSidebarContainer>
  );
};
